import { fetchContactsPathAware, isLocationLookupIntent, sortAndLimitContacts } from "../contacts/router";
import { enqueueFeedbackEvent } from "./feedbackQueue";
import { submitTrainingFeedbackQueue } from "./feedbackSubmit";
import { speak } from "../speech/speak";
import {
  clampForSpeech,
  isDirectContactIntent,
  LOW_CONFIDENCE_SCORE_THRESHOLD,
  shouldSpeakAnswers,
  shouldSuggestContactsFromKb,
} from "./trainingPanelLogic";
import type { AskContext, AskResult } from "./trainingAskTypes";
import type { KbLane } from "../kb/types";
import { expandPreferredPrimaryTagsFromKb, inferPreferredPrimaryTags, strictContactSelection } from "./trainingAskContactHelpers";
import type { KbPhaseState } from "./trainingAskKbPhase";

export async function runTrainingContactsAndFinalize(params: {
  q: string;
  ctx: AskContext;
  contactLimit: number;
  contactFetchLimit: number;
  requestedLane: KbLane;
  guidancePurpose: "contacts" | "policy" | "advice" | "pricing";
  guidanceProduct: "all" | "sheds" | "steel";
  guidanceSource: "user_selected" | "auto_rules" | "default";
  kb: KbPhaseState;
}): Promise<AskResult> {
  const { q, ctx, contactLimit, contactFetchLimit, requestedLane, guidancePurpose, guidanceProduct, guidanceSource, kb } =
    params;
  console.log("[training/ask][debug] finalize start", {
    requestedLane,
    queryPreview: (q || "").slice(0, 120),
    kbMode: kb.out?.mode || null,
    kbSeedContacts: kb.contacts?.length ?? 0,
    kbSeedContactsError: kb.contactsError || null,
  });
  const { out } = kb;
  let { mapped, needsClarification, contacts, contactsWhy, contactsError, answerMode } = kb;
  const topScore = mapped.hits?.[0]?.score ?? 1;

  const direct = requestedLane === "contacts" && isDirectContactIntent(q, isLocationLookupIntent);
  const answerEscalationIntent =
    requestedLane === "answers" &&
    /(who\s+(do|should)\s+i\s+(talk|call|contact)|who handles|escalat|point of contact|who owns)/i.test(q);
  const preferredTags = inferPreferredPrimaryTags(q);
  const policySupport =
    requestedLane === "contacts" &&
    shouldSuggestContactsFromKb(out, LOW_CONFIDENCE_SCORE_THRESHOLD) &&
    preferredTags.length > 0;
  const answerSupplementContacts =
    requestedLane === "answers" &&
    (answerEscalationIntent || topScore < LOW_CONFIDENCE_SCORE_THRESHOLD || out.mode === "no_answer");
  const contactsNoAnswerRecovery = requestedLane === "contacts" && out.mode === "no_answer";
  console.log("[training/ask][debug] finalize branch flags", {
    requestedLane,
    direct,
    policySupport,
    answerSupplementContacts,
    contactsNoAnswerRecovery,
  });

  if (direct || policySupport || answerSupplementContacts || contactsNoAnswerRecovery) {
    const trustOverride =
      ctx.trustLevelOverride === 1 || ctx.trustLevelOverride === 2 || ctx.trustLevelOverride === 3
        ? ctx.trustLevelOverride
        : null;

    let people = await fetchContactsPathAware({
      q,
      limit: contactFetchLimit,
      organizationId: ctx.organizationId ?? null,
      locationId: ctx.kbLocationId ?? null,
      trustLevelOverride: trustOverride,
    });
    console.log("[training/ask][debug] finalize contacts fetch initial", {
      ok: people.ok,
      resultCount: people.ok ? people.results.length : 0,
      error: people.ok ? null : people.error,
      usedLocationId: ctx.kbLocationId ?? null,
    });

    if ((direct || contactsNoAnswerRecovery) && people.ok && (people.results?.length ?? 0) === 0 && ctx.kbLocationId) {
      people = await fetchContactsPathAware({
        q,
        limit: contactFetchLimit,
        organizationId: ctx.organizationId ?? null,
        locationId: null,
        trustLevelOverride: trustOverride,
      });
      console.log("[training/ask][debug] finalize contacts fetch org-wide retry", {
        ok: people.ok,
        resultCount: people.ok ? people.results.length : 0,
        error: people.ok ? null : people.error,
      });
    }

    const why: "direct" | "policy_support" = direct ? "direct" : "policy_support";
    if (requestedLane === "contacts" && out.mode === "no_answer") {
      answerMode = "contacts";
    }

    const kbExpandedTags = expandPreferredPrimaryTagsFromKb(q, out.sources || []);
    const tags = Array.from(new Set([...preferredTags, ...kbExpandedTags]));
    if (direct && tags.length === 0) {
      tags.push("primary_dealer_contact");
    }

    if (people.ok) {
      const strict = strictContactSelection({
        rows: people.results,
        requiredTags: tags,
        limit: contactFetchLimit,
        fallback: people.generalFallback,
      });
      console.log("[training/ask][debug] finalize strict selection", {
        tags,
        strictSelectedCount: strict.selected.length,
        exactMatchCount: strict.exactMatchCount,
        relatedTagSuggestions: strict.relatedTagSuggestions,
        generalFallback: people.generalFallback || null,
      });
      const best = sortAndLimitContacts(strict.selected, contactLimit, tags);
      console.log("[training/ask][debug] finalize post-sort contacts", {
        contactLimit,
        bestCount: best.length,
        bestNames: best.map((c: any) => c?.name || c?.title || "unknown").slice(0, 4),
      });

      contacts = best;
      contactsWhy = why;
      contactsError = null;

      if (answerSupplementContacts && best.length) {
        mapped = {
          ...mapped,
          answer: `${mapped.answer}\n\nIf you want to escalate or confirm, recommended contacts are listed below.`,
        };
      }

      if (best.length) {
        if (requestedLane === "contacts" && out.mode === "no_answer") {
          const names = best
            .map((c: any) => String(c?.name || "").trim())
            .filter(Boolean)
            .slice(0, 2);
          const intro =
            names.length > 0
              ? `I found contact routing for this request. Start with ${names.join(" and ")} below.`
              : "I found contact routing for this request. Start with the recommended contacts below.";
          mapped = {
            ...mapped,
            answer: intro,
            hits: mapped.hits || [],
          };
          needsClarification = false;
        }
      }

      if (strict.exactMatchCount === 0 && strict.relatedTagSuggestions.length > 0) {
        const suggestionLine = `No exact tagged contact match was found for this request. Try a follow-up search using one of: ${strict.relatedTagSuggestions.join(", ")}.`;
        mapped = {
          ...mapped,
          answer: mapped.answer ? `${mapped.answer}\n\n${suggestionLine}` : suggestionLine,
        };
      }
      if (requestedLane === "contacts" && out.mode === "no_answer" && best.length === 0) {
        mapped = {
          ...mapped,
          answer:
            "I could not find a contact-directory match for this request yet. Please verify your organization context and contact tags, or open Admin -> Contacts to add a general_support fallback contact.",
        };
      }
    } else {
      contacts = [];
      contactsWhy = why;
      contactsError = people.error;
      if (requestedLane === "contacts" && out.mode === "no_answer") {
        mapped = {
          ...mapped,
          answer:
            "I could not complete contacts lookup right now. Please verify you are signed in to the correct organization, then try again.",
        };
      }
    }
  }

  let speakText: string | null = null;
  if (out.mode === "answer" || out.mode === "suggested_answer") {
    const toSay = clampForSpeech(out.answer);
    if (shouldSpeakAnswers() && toSay) speakText = toSay;
  }

  const isContactServed =
    (direct && (contacts?.length || 0) > 0) || (contactsWhy === "direct" && (contacts?.length || 0) > 0);

  let didEnqueue = false;

  if (!isContactServed) {
    if ((mapped.hits?.length || 0) === 0) {
      enqueueFeedbackEvent({
        version: ctx.appVersion,
        query: q,
        reason: "no_hits",
        queryKind: ctx.queryKind,
        threadId: ctx.threadId ?? undefined,
        threadTurn: ctx.threadTurn,
        bb_tags: ["kb_gap"],
        answerPreview: mapped.answer,
        locationId: ctx.locationId,
        locationName: ctx.locationName,
        salespersonFirstName: ctx.salespersonFirstName,
      });
      didEnqueue = true;
    } else if ((mapped.hits?.[0]?.score ?? 1) < LOW_CONFIDENCE_SCORE_THRESHOLD) {
      const topHits = mapped.hits.slice(0, 3);
      enqueueFeedbackEvent({
        version: ctx.appVersion,
        query: q,
        reason: "low_confidence",
        queryKind: ctx.queryKind,
        threadId: ctx.threadId ?? undefined,
        threadTurn: ctx.threadTurn,
        topHitIds: topHits.map((h: any) => h.id),
        topHitTitles: topHits.map((h: any) => h.title),
        topHitScores: topHits.map((h: any) => h.score),
        answerPreview: mapped.answer,
        locationId: ctx.locationId,
        locationName: ctx.locationName,
        salespersonFirstName: ctx.salespersonFirstName,
      });
      didEnqueue = true;
    }

    if (didEnqueue && ctx.webhookUrl) {
      await submitTrainingFeedbackQueue({
        webhookUrl: ctx.webhookUrl,
        appVersion: ctx.appVersion,
        locationId: ctx.locationId,
        locationName: ctx.locationName,
        salespersonFirstName: ctx.salespersonFirstName,
      });
    }
  }

  if (speakText) {
    speak(speakText, { profile: "manager" });
  }

  // KB marks no_answer as "needs clarification"; directory contacts mean we still delivered value.
  if ((contacts?.length ?? 0) > 0) {
    needsClarification = false;
  }
  console.log("[training/ask][debug] finalize end", {
    requestedLane,
    answerMode,
    contactsCount: contacts?.length ?? 0,
    contactsWhy,
    contactsError,
    needsClarification,
    answerPreview: (mapped.answer || "").slice(0, 180),
  });

  return {
    ok: true,
    mapped,
    needsClarification,
    contacts,
    contactsWhy,
    contactsError,
    locationCards: [],
    locationError: null,
    speakText: null,
    answerMode,
    guidancePurpose: out.guidancePurpose ?? guidancePurpose,
    guidanceProduct: out.guidanceProduct ?? guidanceProduct,
    guidanceSource: out.guidanceSource ?? guidanceSource,
    confidenceBand: out.confidenceBand,
    requestedLane,
    // Keep lane state aligned with the user's explicit lane selection.
    // Answer mode may still switch to contacts/location fallback when needed.
    effectiveLane: requestedLane,
    fresh_achievements: out.fresh_achievements,
  };
}
